'use client'

import { useTranslations } from 'next-intl'

import Disclosure from './Disclosure'
import StepLabel, { emphasiseVerb } from './StepLabel'
import TurnSteps from './TurnSteps'
import { splitDuration } from './duration'
import type { TurnStep } from './turnModel'

type WorkedSectionProps = {
  steps: TurnStep[]
  /** The reply is still streaming. */
  streaming: boolean
  /** The key of the step the turn is working on, if any. */
  liveStepKey?: string
  /** Whether a "Worked" header folds the steps. Without one the steps sit open, in the same tree. */
  showHeader: boolean
  /** Whether the section is open until the reader toggles it. */
  defaultExpanded: boolean
  /** How long the finished turn took; the header stays a plain "Worked" without it or under a second. */
  durationMs?: number
}

/** The steps of a turn, under a "Worked" header once there is one. */
export default function WorkedSection({
  steps,
  streaming,
  liveStepKey,
  showHeader,
  defaultExpanded,
  durationMs
}: WorkedSectionProps) {
  const t = useTranslations('features.AssistantV2')

  return (
    <Disclosure
      headerless={!showHeader}
      defaultExpanded={defaultExpanded}
      label={<StepLabel>{workedLabel()}</StepLabel>}
    >
      <TurnSteps steps={steps} streaming={streaming} liveStepKey={liveStepKey} />
    </Disclosure>
  )

  function workedLabel() {
    const { minutes, seconds } = splitDuration(durationMs ?? 0)
    if (minutes > 0) return t.rich('workedForMinutes', { verb: emphasiseVerb, minutes, seconds })
    if (seconds > 0) return t.rich('workedForSeconds', { verb: emphasiseVerb, seconds })
    return t.rich('worked', { verb: emphasiseVerb })
  }
}
